/**
 * Export orchestration.
 *
 * Order of operations (Section 9.5): validate → build files → HARD consistency
 * gate → zip. The gate runs on the serialized output, so a broken pack can
 * never be produced even if a builder has a bug.
 */

import { hasBlockingErrors, validateProject, type Issue } from "../model/validation";
import type { Project } from "../model/types";
import { assertGuideConsistency, ConsistencyError } from "./consistency";
import { buildExportFiles, type ExportFile } from "./files";
import { bytesToBlob, filesToZipBytes } from "./zip";

export interface ExportResult {
  blob: Blob;
  filename: string;
  files: ExportFile[];
}

export class ExportBlockedError extends Error {
  constructor(
    message: string,
    public readonly issues: Issue[],
  ) {
    super(message);
    this.name = "ExportBlockedError";
  }
}

export function safeFilename(project: Project): string {
  const base = project.namespace || "guidebook";
  return `${base}.mcaddon`;
}

/**
 * Build the full pack and return a downloadable blob. Throws
 * `ExportBlockedError` on validation errors, or `ConsistencyError` if the
 * cross-file guide-id gate fails.
 */
export function exportProject(project: Project): ExportResult {
  const issues = validateProject(project);
  if (hasBlockingErrors(issues)) {
    throw new ExportBlockedError("Project has validation errors and cannot be exported.", issues);
  }

  const files = buildExportFiles(project);

  // HARD GATE: throws ConsistencyError if guide ids diverge.
  assertGuideConsistency(project, files);

  const bytes = filesToZipBytes(files);
  return {
    blob: bytesToBlob(bytes),
    filename: safeFilename(project),
    files,
  };
}

export { ConsistencyError };
export type { ExportFile };
